import { autoinject } from "aurelia-dependency-injection";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import * as moment from "moment";
import { CarbonAccountingSummaryReportDataModel, GetCarbonAccountingSummaryReportDataRequest, CarbonAccountingSummaryData, FinancialYearTypeEnum, CarbonAccountingCustomReportFilterModel } from "../../../../models/carbon-accounting/carbon-accounting.models";
import { VoyageService } from "../../../../services/voyage/voyage.service";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import * as Enumerable from "linq";
import { observable, computedFrom } from "aurelia-binding";
import { CargoTypeDistributionModel } from "../../../../models/vetting/vetting.models";
import { CarbonAccountingEnrollmentService } from "../../../../services/carbon-accounting/carbon-accounting-enrollment.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { LookupValue } from "../../../../models/lookups/lookup.models";

@autoinject
export class CarbonAccountingSummary {

    public summaryReportData: CarbonAccountingSummaryReportDataModel;
    public isRightShipUser: boolean = false;
    public isMobileFilterOpen: boolean;
    public isEmissions: boolean = true;
    public yearRangeFilterActive: boolean;
    public showYearRangeFilter: boolean;
    public showYearFilter: boolean;
    public isQuarterly: boolean;
    public raportTypes: { id: number, name: string }[] = [
        {
            id: 1,
            name: "Quarterly"
        }, {
            id: 2,
            name: "Year on Year"
        },
    ];
    public ghgRatings: string[] = [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G'
    ];
    public selectedRaportType: any = this.raportTypes[0];
    public selectedCustomerId?: number;
    public customerFinancialYearTypeId?: number;

    public customFilters: CarbonAccountingCustomReportFilterModel[] = [];

    private userCustomerId: number = null;

    @computedFrom("customerFinancialYearTypeId")
    public get financialYearType(): string {
        if (!this.summaryReportData)
            return "";

        switch (this.customerFinancialYearTypeId) {
            case FinancialYearTypeEnum.January:
                return "January";
            case FinancialYearTypeEnum.April:
                return "April";
            case FinancialYearTypeEnum.July:
                return "July";
            case FinancialYearTypeEnum.October:
                return "October";
            default:
                return "";
        }
    }

    @computedFrom("customerFinancialYearTypeId")
    public get financialStartMessage(): string {
        if (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January) {
            return "Calendar year starting from " + this.financialYearType;
        } else {
            return "Financial year starting from " + this.financialYearType;
        }
    }

    constructor(private dialogClientService: DialogClientService, private voyageService: VoyageService, userClaimsService: UserClaimsService,
        private carbonAccountingService: CarbonAccountingEnrollmentService, private customerService: CommonCustomerService) {
        this.isRightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
    }

    public get isSubFilterOpen(): boolean {
        return this.yearRangeFilterActive || (this.customFilters && this.customFilters.filter(x => x["open"] == true).length > 0);
    }

    public get mobileFilterSelected() {
        return this.yearRangeFilterActive || (this.customFilters && this.customFilters.filter(x => x["open"] == true).length > 0);
    }

    public get hasCargoTypesAvailable() {
        let hasCargo = false;
        if (this.summaryTableData) {
            this.summaryTableData.forEach(x => {
                if (x.cargoTypes > 0) { hasCargo = true; }
            });
        }
        return hasCargo
    }

    activate(model) {
        this.voyageService.getCarbonAccountingVoyageSubmissionYears(this.selectedCustomerId ? this.selectedCustomerId : 0)
            .then(r => {
                const maxYear = Math.max.apply(Math, [...new Set(r.voyageSubmissionYears)]);
                this.year = this.customerFinancialYearTypeId == FinancialYearTypeEnum.January ? maxYear : (maxYear - 1).toString() + '-' + maxYear;
            });

        if (model != null && model.year != null && model.year > 0) {
            this.isQuarterly = true;
            this.year = model.year;
        }
    }

    attached() {
        this.setIsEmission(true);
        this.getCustomerEnrollmentData();
        this.reloadSummaryReportData();
        this.getCustomFilters();
    }

    getCustomerEnrollmentData() {
        if (this.selectedCustomerId || !this.isRightShipUser) {
            this.carbonAccountingService.GetCaEnrollmentDataForSummary(this.selectedCustomerId).then(response => {
                if (!response)
                    return;

                this.customerFinancialYearTypeId = response.financialYearTypeId;
            });
        }
    }

    async reloadSummaryReportData() {
        this.summaryTableData = [];
        const toYear = !this.isQuarterly && this.yearRangeIsApplied && this.yearSecondFilter ? this.yearSecondFilter : (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January ? this.year : (this.year ? this.year.split('-')[1] : null));
        let fromYear = !this.isQuarterly && this.yearRangeIsApplied && this.yearFirstFilter ? this.yearFirstFilter : (this.customerFinancialYearTypeId == FinancialYearTypeEnum.January ? this.year : (this.year ? this.year.split('-')[1] : null));

        if (toYear - fromYear > 4) {
            fromYear = toYear - 3;
        }

        const customFitlerData = {};
        if (this.customFilters && this.customFilters.length > 0)
            this.customFilters.forEach(f => {
                customFitlerData[f.internalColumn.substring(f.internalColumn.length - 1)] = f["selectedValue"] && f["selectedValue"].trim() != "" ? f["selectedValue"] : null;
            });

        const request: GetCarbonAccountingSummaryReportDataRequest = {
            customerId: this.selectedCustomerId,
            fromYear: fromYear,
            toYear: toYear,
            periodTypeId: this.isQuarterly ? PeriodTypeEnum.Quarterly : PeriodTypeEnum.Annual,
            customFilterValue1: Object.keys(customFitlerData).indexOf("1") > -1 ? customFitlerData["1"] : null,
            customFilterValue2: Object.keys(customFitlerData).indexOf("2") > -1 ? customFitlerData["2"] : null,
            customFilterValue3: Object.keys(customFitlerData).indexOf("3") > -1 ? customFitlerData["3"] : null,
            customFilterValue4: Object.keys(customFitlerData).indexOf("4") > -1 ? customFitlerData["4"] : null,
            customFilterValue5: Object.keys(customFitlerData).indexOf("5") > -1 ? customFitlerData["5"] : null,
            customFilterValue6: Object.keys(customFitlerData).indexOf("6") > -1 ? customFitlerData["6"] : null
        };

        this.summaryReportData = await this.voyageService.getCarbonAccountingSummaryReportData(request);

        this.selectedVolumesCargoUnit = this.cargoTypeChartsDefaultCargoUnit;
        this.selectedEmissionsCargoUnit = this.cargoTypeChartsDefaultCargoUnit;

        this.initSummaryTable();
        this.initGhgEmissionsChart();
        this.initGhgRatingChart();
        this.initEmissionsPerVesselTypeChart();
        this.initNumberOfVesselsHiredChart();
        this.initEmissionsPerCargoTypeChart();
        this.initCargoVolumesChart();
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        // this.appliedFilter = this.filter;
        // this.selectedStatusFilter = [];
        // let column = this.columns.filter(x => x.name == 'status')[0];
        // column.filters = [];

        // if (this.mobileSelectedFilter && this.mobileSelectedFilter.length > 0) {
        //     for (let item of this.mobileSelectedFilter)
        //         this.filterClick({ id: item, name: '' });
        // } else {
        //     this.setItemSource();
        // }
    }

    toggleYearRangeFilters() {
        this.yearRangeFilterActive = !this.yearRangeFilterActive;
        this.selectedFilter = {
            title: "Date Range",
            clear: () => {
                this.clearYearRangeFilter();
            },
            apply: () => {
                const filterValue: { low: number, high: number } = { low: this.mobileYearRangeFilter.low, high: this.mobileYearRangeFilter.high }
                this.applyYearRangeFilter(filterValue);
            }
        }
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        if (this.customFilters && this.customFilters.length > 0)
            this.customFilters.forEach(x => x["open"] = false)

        this.yearRangeFilterActive = false;
    }

    closeAllFilters() {
        this.yearRangeFilterActive = false;
        if (this.customFilters && this.customFilters.length > 0)
            this.customFilters.forEach(x => {
                x["open"] = false;
            });
    }

    viewCargoTypesDialog() {
        const cargoTypeDistributions: CargoTypeDistributionModel[] = [];

        const cargoTypes = Enumerable.from(this.summaryReportData.caReportDataPerCargoType)
            .select(x => x.cargoTypeName)
            .distinct()
            .toArray();
        const cargoUnits = Enumerable.from(this.summaryReportData.caReportDataPerCargoType)
            .select(x => x.cargoUnit)
            .distinct()
            .toArray();
        const periods = Enumerable.from(this.summaryReportData.caReportDataPerCargoType)
            .select(x => {
                return this.isQuarterly ? x.customerDateQuarterSkey : x.year;
            })
            .distinct()
            .toArray();

        for (const cargoUnit of cargoUnits) {
            for (const cargoType of cargoTypes) {
                for (const period of periods) {
                    if (this.summaryReportData.caReportDataPerCargoType.filter(x => x.cargoUnit == cargoUnit && x.cargoTypeName == cargoType).length == 0) continue;

                    let emissions = 0;
                    const year = Enumerable.from(this.summaryReportData.caReportDataPerCargoType)
                        .where(x => x.customerDateQuarterSkey == period && x.cargoUnit == cargoUnit && x.cargoTypeName == cargoType)
                        .select(x => x.year).firstOrDefault();

                    if (this.isQuarterly) {
                        emissions = Enumerable.from(this.summaryReportData.caReportDataPerCargoType)
                            .where(x => x.customerDateQuarterSkey == period && x.cargoUnit == cargoUnit && x.cargoTypeName == cargoType)
                            .select(x => x.totalGhgEmissions)
                            .sum(x => x);
                    } else {
                        emissions = Enumerable.from(this.summaryReportData.caReportDataPerCargoType)
                            .where(x => x.year == period && x.cargoUnit == cargoUnit && x.cargoTypeName == cargoType)
                            .select(x => x.totalGhgEmissions)
                            .sum(x => x);
                    }
                    cargoTypeDistributions.push({
                        cargoType: cargoType,
                        cargoTypeDistributionId: 0,
                        cargoUnit: cargoUnit,
                        periodValue: period,
                        totalGhgEmissions: emissions,
                        year: year
                    });
                }
            }
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CargoTypesDialog,
            {
                items: cargoTypeDistributions,
                isQuarterly: this.isQuarterly
            }, true, false, false);
    }

    raportTypeChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.item.id === $event.detail.oldValue.id))
            return;

        const reportTypeId = $event.detail.item.id;
        switch (reportTypeId) {
            case 1:
                this.showYearFilter = true;
                this.showYearRangeFilter = false;
                this.yearRangeIsApplied = false;
                this.isQuarterly = true;
                break;

            case 2:
                this.showYearFilter = false;
                this.showYearRangeFilter = true;
                this.yearRangeIsApplied = true;
                this.isQuarterly = false;
                break;
        }
        this.closeAllFilters();
        this.reloadSummaryReportData();
    }

    selectedCustomerChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.value === $event.detail.oldValue))
            return;

        this.getCustomFilters()
        this.getCustomerEnrollmentData();
        this.voyageService.getCarbonAccountingVoyageSubmissionYears(this.selectedCustomerId ? this.selectedCustomerId : 0)
            .then(r => {
                const maxYear = Math.max.apply(Math, [...new Set(r.voyageSubmissionYears)]);
                this.year = this.customerFinancialYearTypeId == FinancialYearTypeEnum.January ? maxYear.toString() : (maxYear - 1).toString() + '-' + maxYear;
            });

        this.reloadSummaryReportData();
    }

    //#region Yeah Range filter

    public yearRangeDefaults: { low: number, high: number } = { low: moment().year() - 3, high: moment().year() };
    @observable()
    public yearFirstFilter: number = this.yearRangeDefaults.low;
    @observable()
    public yearSecondFilter: number = this.yearRangeDefaults.high;
    public mobileYearRangeFilter: { low: number, high: number };
    public yearRangeIsApplied: boolean = false;

    @observable
    year: any;

    get years() {
        const yearsRange = Enumerable.range(this.yearRangeDefaults.low - 20, (this.yearRangeDefaults.high - this.yearRangeDefaults.low) + 21).orderByDescending(x => x).toArray();
        const financialYearsToDisplay: string[] = [];
        yearsRange.forEach(x => {
            const financialYear = x - 1;

            financialYearsToDisplay.push(this.customerFinancialYearTypeId == FinancialYearTypeEnum.January ? x.toString() : financialYear.toString() + '-' + x.toString())
        });

        return financialYearsToDisplay;
    }

    public applyYearRangeFilter(filterValue: { low: number, high: number } = null) {

        if (filterValue) {
            this.yearFirstFilter = filterValue.low;
            this.yearSecondFilter = filterValue.high;
            this.yearRangeIsApplied = true;
        }

        this.reloadSummaryReportData();
        this.toggleYearRangeFilters();
    }

    public clearYearRangeFilter() {
        this.yearFirstFilter = this.yearRangeDefaults.low;
        this.yearSecondFilter = this.yearRangeDefaults.high;
        this.mobileYearRangeFilter = { low: null, high: null };
        this.yearRangeIsApplied = false;

        this.reloadSummaryReportData();
        this.toggleYearRangeFilters();
    }

    //#endregion

    selectedYearChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.item.id === $event.detail.oldValue.id))
            return;

        this.reloadSummaryReportData();
    }

    yearFirstFilterChanged() {
        window.setTimeout(() => {
            this.yearSecondFilter = (this.yearFirstFilter) + 3;
        }, 500)
    }

    yearSecondFilterChanged() {
        window.setTimeout(() => {
            this.yearFirstFilter = (this.yearSecondFilter) - 3;
        }, 500)
    }

    //#region Summary table

    public summaryTableData: CarbonAccountingSummaryData[];

    initSummaryTable() {
        if (!this.summaryReportData || !this.summaryReportData.caSummaryDataPerPeriod)
            return;

        const periodTypeId = this.isQuarterly ? PeriodTypeEnum.Quarterly : PeriodTypeEnum.Annual;
        this.summaryTableData = Enumerable.from(this.summaryReportData.caSummaryDataPerPeriod)
            .where(x => x.periodTypeSkey == periodTypeId)
            .groupBy(x => x.customerDatePeriodValue)
            .select(x => {
                const summary: CarbonAccountingSummaryData = {
                    customerDatePeriodValue: x.key(),
                    periodTypeSkey: x.first().periodTypeSkey,
                    totalGhgEmissions: x.sum(y => y.totalGhgEmissions),
                    totalDistance: x.sum(y => y.totalDistance),
                    totalDwt: x.sum(y => y.totalDwt),
                    totalTeu: x.sum(y => y.totalTeu),
                    totalCBM: x.sum(y => y.totalCBM),
                    cargoTypes: x.sum(y => y.cargoTypes),
                    voyages: x.sum(y => y.voyages),
                    vessels: x.sum(y => y.vessels)
                };

                return summary;
            })
            .toArray();
    }

    //#endregion

    //#region GHG Emissions chart

    public ghgEmissionsChartSettings: ChartSettings;
    public ghgEmissionsChartData: { name: string, totalValue: number, quarters: { identifier: string, value: number }[] }[];
    public ghgTotalAverageIntensity: number;

    initGhgEmissionsChart() {
        if (!this.summaryReportData || !this.summaryReportData.caSummaryDataPerPeriod)
            return;

        const chartData: {
            name:string,
            totalValue: number,
            year: number,
            quarters: {
                identifier: string,
                value: number
            }[]
        }[] = [];

        const filteredData = Enumerable.from(this.summaryReportData.caSummaryDataPerPeriod).where(x => x.periodTypeSkey == PeriodTypeEnum.Quarterly
            && (this.isEmissions || (this.selectedComputedUnit && x.computedUnit == this.selectedComputedUnit.name))).toArray();

        if (!filteredData || filteredData.length == 0) {
            this.ghgEmissionsChartSettings = null;
            this.ghgEmissionsChartData = [];
            this.ghgTotalAverageIntensity = null;
            return;
        }

        for (const res of filteredData) {

            const quarterIdentifier = `Q${res.customerDatePeriodValue}`;
            let value = 0;
            if (this.isEmissions)
                value = Enumerable.from(filteredData).where(x => x.year == res.year && x.customerDatePeriodValue == res.customerDatePeriodValue).sum(x => x.totalGhgEmissions);
            else
                value = Enumerable.from(filteredData).where(x => x.year == res.year && x.customerDatePeriodValue == res.customerDatePeriodValue).sum(x => x.totalIntensity);

            if (!this.isQuarterly || this.isEmissions) {

                if (chartData.length == 0) {
                    chartData.push({
                        name: `${res.year}`,
                        quarters: [{ identifier: quarterIdentifier, value: value }],
                        totalValue: 0,
                        year: res.year
                    });
                } else {
                    const dataToChange = chartData.find(x => {
                        return x.name == `${res.year}`;
                    });
                    if (dataToChange) {
                        const index = chartData.findIndex(x => x == dataToChange);
                        if (Enumerable.from<{ identifier: string }>(dataToChange.quarters).where(x => x.identifier == quarterIdentifier).any())
                            continue;

                        dataToChange.quarters.push({
                            identifier: quarterIdentifier,
                            value: value
                        });
                        chartData[index] = dataToChange;
                    } else {
                        chartData.push({
                            name: `${res.year}`,
                            quarters: [{ identifier: quarterIdentifier, value: value }],
                            totalValue: 0,
                            year: res.year
                        });
                    }

                }
            } else {
                chartData.push({
                    name: quarterIdentifier,
                    quarters: [{ identifier: quarterIdentifier, value: value }],
                    totalValue: 0,
                    year: res.year
                });
            }

        }

        chartData.forEach(x => {
            x.totalValue = this.isEmissions ? Enumerable.from(x.quarters).sum(y => y.value) : Enumerable.from(this.summaryReportData.caSummaryDataPerPeriod).where(y => y.periodTypeSkey == PeriodTypeEnum.Annual
                && this.selectedComputedUnit && y.computedUnit == this.selectedComputedUnit.name && x.year == y.year).select(y => y.totalIntensity ? y.totalIntensity : 0).firstOrDefault();
            x.quarters = Enumerable.from(x.quarters).orderBy(y => y.identifier).toArray();
        });

        if (!this.isEmissions && this.isQuarterly) {
            this.ghgTotalAverageIntensity = Enumerable.from(this.summaryReportData.caSummaryDataPerPeriod).where(x => x.periodTypeSkey == PeriodTypeEnum.Annual
                && this.selectedComputedUnit && x.computedUnit == this.selectedComputedUnit.name).select(x => x.totalIntensity).firstOrDefault();
        }
        else {
            this.ghgTotalAverageIntensity = null;
        }

        if (chartData)
            if (this.customerFinancialYearTypeId != FinancialYearTypeEnum.January) {
                chartData.map(x => {
                    if (this.isEmissions) {
                        x.name = `${Number(x.name) - 1} - ${x.name}`
                    } else {
                        return x.name
                    }
                })
            }
        this.ghgEmissionsChartData = chartData;

        const maxValue = Enumerable.from(chartData).max(x => x.totalValue);
        const namesToConvert = Enumerable.from(filteredData)
            .select(x => `Q${x.customerDatePeriodValue}`)
            .distinct()
            .orderBy(x => x)
            .toArray();

        this.ghgEmissionsChartSettings = this.generateChartSettings(maxValue * 1.1);
        this.ghgEmissionsChartSettings.legends = this.getChartLegend(namesToConvert);
    }

    setIsEmission(x: boolean) {
        this.isEmissions = x;
        this.initGhgEmissionsChart();
    }

    //#endregion

    //#region GHG Rating fleet distribution

    public ghgRatingChartSettings: ChartSettings;
    public ghgRatingsChartData: { ghgRating: string, periods: { identifier: string, value: number }[] }[];

    initGhgRatingChart() {
        if (!this.summaryReportData || !this.summaryReportData.caReportingDataPerEmissionsRating)
            return;

        this.ghgRatingsChartData = [];
        const chartData: {
            ghgRating: string,
            periods: {
                identifier: string,
                value: number
            }[]
        }[] = [];
        const filteredData = Enumerable.from(this.summaryReportData.caReportingDataPerEmissionsRating).where(x => x.emissionsRating != 'U').toArray();

        if (!filteredData || filteredData.length == 0) {
            this.ghgRatingChartSettings = null;
            this.ghgRatingsChartData = [];
            return;
        }

        const periods = Enumerable.from(filteredData)
            .select(x => {
                return this.isQuarterly ? `Q${x.customerDateQuarterSkey}` : `${x.year}`;
            })
            .distinct()
            .toArray();
        const ratings = Enumerable.from(filteredData)
            .select(x => {
                return x.emissionsRating;
            })
            .distinct()
            .orderBy(x => x)
            .toArray();

        for (const rating of ratings) {
            const chartDataToPush: {
                ghgRating: string,
                periods: {
                    identifier: string,
                    value: number
                }[]
            } = {
                ghgRating: rating,
                periods: []
            };
            for (const period of periods) {
                let totalPeriodVessels = 0;
                let periodVesselsPerRating = 0;

                if (this.isQuarterly) {
                    totalPeriodVessels = Enumerable.from(filteredData)
                        .where(x => `Q${x.customerDateQuarterSkey}` == period)
                        .select(x => x.vessels)
                        .sum(x => x);
                    periodVesselsPerRating = Enumerable.from(filteredData)
                        .where(x => `Q${x.customerDateQuarterSkey}` == period && x.emissionsRating == rating)
                        .select(x => x.vessels)
                        .sum(x => x);
                } else {
                    totalPeriodVessels = Enumerable.from(filteredData)
                        .where(x => `${x.year}` == period)
                        .select(x => x.vessels)
                        .sum(x => x);
                    periodVesselsPerRating = Enumerable.from(filteredData)
                        .where(x => `${x.year}` == period && x.emissionsRating == rating)
                        .select(x => x.vessels)
                        .sum(x => x);
                }

                const percentage = periodVesselsPerRating * 100 / totalPeriodVessels;
                chartDataToPush.periods.push({
                    identifier: period,
                    value: percentage
                });
            }
            chartData.push(chartDataToPush);
        }

        let maxValue = Enumerable.from(chartData)
            .select(x => Enumerable.from(x.periods).max(y => y.value))
            .max(x => x);
        maxValue = Math.max(maxValue, 51);

        if (chartData)
            this.ghgRatingsChartData = chartData;

        const numberOfSteps = Math.ceil(maxValue / 10);
        this.ghgRatingChartSettings = this.generateChartSettings(maxValue, numberOfSteps);

        this.ghgRatingChartSettings.legends = this.getChartLegend(periods);
    }

    getGhgRatingPeriodsData(rating: string): any[] {
        if (!this.ghgRatingsChartData)
            return [];

        const filteredData = this.ghgRatingsChartData.filter(x => x.ghgRating == rating)[0];

        if (!filteredData)
            return [];

        return filteredData.periods;
    }

    //#endregion

    //#region GHG emissions per vessel type

    public emissionsPerVesselTypeChartSettings: ChartSettings;
    public emissionsPerVesselTypeChartData: { vesselType: string, periods: { identifier: string, value: number }[] }[];
    public isTypeSelected: boolean = true;
    public vesselTypes: string[];

    initEmissionsPerVesselTypeChart() {
        if (!this.summaryReportData || !this.summaryReportData.caReportDataPerVesselType)
            return;
        const chartData : { vesselType: string, periods: { identifier: string, value: number }[] }[] = [];
        const periodType = this.isQuarterly ? PeriodTypeEnum.Quarterly : PeriodTypeEnum.Annual;
        const filteredResults = Enumerable.from(this.summaryReportData.caReportDataPerVesselType).where(x => x.periodTypeSkey == periodType).toArray();

        if (!filteredResults || filteredResults.length == 0) {
            this.emissionsPerVesselTypeChartSettings = null;
            this.emissionsPerVesselTypeChartData = [];
            return;
        }

        this.vesselTypes = Enumerable.from(this.summaryReportData.caReportDataPerVesselType)
            .select(x => x.vesselType)
            .distinct()
            .toArray();

        const periods = Enumerable.from(filteredResults)
            .select(x => x.customerDatePeriodValue)
            .distinct()
            .orderBy(x => x)
            .toArray();

        for (const vesselType of this.vesselTypes) {
            const chartDataToPush = {
                vesselType: vesselType,
                periods: []
            };
            for (const period of periods) {
                const periodEmissionsPerVesselType = Enumerable.from(filteredResults)
                    .where(x => x.customerDatePeriodValue == period && x.vesselType == vesselType)
                    .select(x => this.isTypeSelected ? x.totalGhgEmissions : x.totalDwt)
                    .sum(x => x);
                chartDataToPush.periods.push({
                    identifier: this.isQuarterly ? `Q${period}` : period,
                    value: periodEmissionsPerVesselType
                });
            }
            chartData.push(chartDataToPush);
        }

        const maxValue = Enumerable.from(chartData)
            .select(x => Enumerable.from(x.periods).max(y => y.value))
            .max(x => x);

        if (chartData)
            this.emissionsPerVesselTypeChartData = chartData;

        this.emissionsPerVesselTypeChartSettings = this.generateChartSettings(maxValue * 1.1, 5);

        const namesToConvert = Enumerable.from(periods)
            .select(x => this.isQuarterly ? `Q${x}` : `${x}`)
            .toArray();

        this.emissionsPerVesselTypeChartSettings.legends = this.getChartLegend(namesToConvert);
    }

    getEmissionsPerVesselTypePeriodsData(type: string): any[] {
        if (!this.emissionsPerVesselTypeChartData)
            return;

        const filteredData = this.emissionsPerVesselTypeChartData.filter(x => x.vesselType == type)[0];
        if (!filteredData)
            return [];

        return filteredData.periods;
    }

    setIsTypeSelected(x: boolean) {
        this.isTypeSelected = x;
        this.initEmissionsPerVesselTypeChart();
    }

    //#endregion

    //#region GHG Emissions chart properties

    public numberOfVesselsHiredChartSettings: ChartSettings;
    public numberOfVesselsHiredChartData: { vesselType: string, periods: { identifier: string, value: number }[] }[];

    initNumberOfVesselsHiredChart() {
        if (!this.summaryReportData || !this.summaryReportData.caReportDataPerVesselType)
            return;

        const chartData: { vesselType: string, periods: { identifier: string, value: number }[] }[] = [];
        const periodType = this.isQuarterly ? PeriodTypeEnum.Quarterly : PeriodTypeEnum.Annual;
        const filteredResults = Enumerable.from(this.summaryReportData.caReportDataPerVesselType).where(x => x.periodTypeSkey == periodType).toArray();

        if (!filteredResults || filteredResults.length == 0) {
            this.numberOfVesselsHiredChartSettings = null;
            this.numberOfVesselsHiredChartData = [];
            return;
        }

        const periods = Enumerable.from(filteredResults)
            .select(x => x.customerDatePeriodValue)
            .distinct()
            .orderBy(x => x)
            .toArray();

        for (const vesselType of this.vesselTypes) {
            const chartDataToPush = {
                vesselType: vesselType,
                periods: []
            };
            for (const period of periods) {
                const periodVesselsPerVesselType = Enumerable.from(filteredResults)
                    .where(x => x.customerDatePeriodValue == period && x.vesselType == vesselType)
                    .select(x => x.vessels)
                    .sum(x => x);
                chartDataToPush.periods.push({
                    identifier: this.isQuarterly ? `Q${period}` : period,
                    value: periodVesselsPerVesselType
                });
            }
            chartData.push(chartDataToPush);
        }

        const maxValue = Enumerable.from(chartData)
            .select(x => Enumerable.from(x.periods).max(y => y.value))
            .max(x => x);

        if (chartData)
            this.numberOfVesselsHiredChartData = chartData;

        this.numberOfVesselsHiredChartSettings = this.generateChartSettings(maxValue * 1.1, 5);

        const namesToConvert = Enumerable.from(periods)
            .select(x => this.isQuarterly ? `Q${x}` : `${x}`)
            .toArray();
        this.numberOfVesselsHiredChartSettings.legends = this.getChartLegend(namesToConvert);
    }

    getNumberOfVesselsHiredPeriodsData(type: string): any[] {
        if (!this.numberOfVesselsHiredChartData)
            return;

        const filteredData = this.numberOfVesselsHiredChartData.filter(x => x.vesselType == type)[0];
        if (!filteredData)
            return [];

        return filteredData.periods;
    }

    //#endregion

    @computedFrom("summaryReportData", "summaryReportData.caReportDataPerCargoType", "summaryReportData.caReportDataPerCargoType.length")
    get cargoTypeChartsDefaultCargoUnit() {
        let unit = "DWT";
        if (this.summaryReportData && this.summaryReportData.caReportDataPerCargoType && this.summaryReportData.caReportDataPerCargoType.length > 0) {
            unit = Enumerable.from(this.summaryReportData.caReportDataPerCargoType).groupBy(x => x.cargoUnit).select(x => { return { cargoUnit: x.key(), Count: x.count() }; }).orderByDescending(x => x.Count).firstOrDefault().cargoUnit;
        }

        return unit ?? "DWT";
    }

    //#region GHG Emissions Per Cargo Type

    public emissionsPerCargoTypeChartSettings: ChartSettings;
    public emissionsPerCargoTypeChartData: { cargoType: string, periods: { identifier: string, value: number }[] }[];
    public selectedEmissionsCargoUnit: string = "DWT";
    public emissionsCargoTypes: string[];

    initEmissionsPerCargoTypeChart() {
        if (!this.summaryReportData || !this.summaryReportData.caReportDataPerCargoType)
            return;

        this.emissionsPerCargoTypeChartData = [];

        const chartData: { cargoType: string, periods: { identifier: string, value: number }[] }[] = [];

        const filteredResults = Enumerable.from(this.summaryReportData.caReportDataPerCargoType).where(x => x.cargoUnit == this.selectedEmissionsCargoUnit).toArray();
        if (!filteredResults || filteredResults.length == 0) {
            this.emissionsPerCargoTypeChartSettings = null;
            this.emissionsPerCargoTypeChartData = [];
            return;
        }

        this.emissionsCargoTypes = Enumerable.from(this.summaryReportData.caReportDataPerCargoType)
            .where(x => x.cargoUnit == this.selectedEmissionsCargoUnit)
            .select(x => x.cargoTypeName)
            .distinct()
            .toArray();
        const periods = Enumerable.from(filteredResults)
            .select(x => {
                return this.isQuarterly ? `Q${x.customerDateQuarterSkey}` : `${x.year}`;
            })
            .distinct()
            .orderBy(x => x)
            .toArray();

        for (const cargoType of this.emissionsCargoTypes) {
            const chartDataToPush = {
                cargoType: cargoType,
                periods: []
            };
            for (const period of periods) {
                let periodEmissionsPerCargoType = 0;

                if (this.isQuarterly) {
                    periodEmissionsPerCargoType = Enumerable.from(filteredResults)
                        .where(x => `Q${x.customerDateQuarterSkey}` == period && x.cargoTypeName == cargoType)
                        .select(x => x.totalGhgEmissions)
                        .sum(x => x);
                } else {
                    periodEmissionsPerCargoType = Enumerable.from(filteredResults)
                        .where(x => `${x.year}` == period && x.cargoTypeName == cargoType)
                        .select(x => x.totalGhgEmissions)
                        .sum(x => x);
                }
                chartDataToPush.periods.push({
                    identifier: period,
                    value: periodEmissionsPerCargoType
                });
            }
            chartData.push(chartDataToPush);
        }

        const maxValue = Enumerable.from(chartData)
            .select(x => Enumerable.from(x.periods).max(y => y.value))
            .max(x => x);

        if (chartData)
            this.emissionsPerCargoTypeChartData = chartData;

        this.emissionsPerCargoTypeChartSettings = this.generateChartSettings(maxValue * 1.1, 5);
        this.emissionsPerCargoTypeChartSettings.legends = this.getChartLegend(periods);

    }

    getEmissionsPerCargoTypePeriodsData(type: string): any[] {
        if (!this.emissionsPerCargoTypeChartData)
            return;

        const filteredData = this.emissionsPerCargoTypeChartData.filter(x => x.cargoType == type)[0];
        if (!filteredData)
            return [];

        return filteredData.periods;
    }

    setEmissionCargoUnit(unit: string) {
        if (!unit)
            return;

        this.selectedEmissionsCargoUnit = unit;
        this.initEmissionsPerCargoTypeChart();
    }

    //#endregion

    //#region Cargo Volumes

    public cargoVolumesChartSettings: ChartSettings;
    public cargoVolumesChartData: { cargoType: string, periods: { identifier: string, value: number }[] }[];
    public selectedVolumesCargoUnit: string = "DWT";
    public volumesCargoTypes: string[];

    initCargoVolumesChart() {
        if (!this.summaryReportData || !this.summaryReportData.caReportDataPerCargoType)
            return;

        const chartData :{ cargoType: string, periods: { identifier: string, value: number }[] }[] = [];

        const filteredResults = Enumerable.from(this.summaryReportData.caReportDataPerCargoType).where(x => x.cargoUnit == this.selectedVolumesCargoUnit).toArray();
        if (!filteredResults || filteredResults.length == 0) {
            this.cargoVolumesChartSettings = null;
            this.cargoVolumesChartData = [];
            return;
        }

        this.volumesCargoTypes = Enumerable.from(this.summaryReportData.caReportDataPerCargoType)
            .where(x => x.cargoUnit == this.selectedVolumesCargoUnit)
            .select(x => x.cargoTypeName)
            .distinct()
            .toArray();
        const periods = Enumerable.from(filteredResults)
            .select(x => {
                return this.isQuarterly ? `Q${x.customerDateQuarterSkey}` : `${x.year}`;
            })
            .distinct()
            .orderBy(x => x)
            .toArray();

        for (const cargoType of this.volumesCargoTypes) {
            const chartDataToPush = {
                cargoType: cargoType,
                periods: []
            };
            for (const period of periods) {
                let periodCargoQtyPerCargoType = 0;

                if (this.isQuarterly) {
                    periodCargoQtyPerCargoType = Enumerable.from(filteredResults)
                        .where(x => `Q${x.customerDateQuarterSkey}` == period && x.cargoTypeName == cargoType)
                        .select(x => this.selectedVolumesCargoUnit == "DWT" ? x.totalDwt : this.selectedVolumesCargoUnit == "TEU" ? x.totalTeu : x.totalCBM)
                        .sum(x => x);
                } else {
                    periodCargoQtyPerCargoType = Enumerable.from(filteredResults)
                        .where(x => `${x.year}` == period && x.cargoTypeName == cargoType)
                        .select(x => this.selectedVolumesCargoUnit == "DWT" ? x.totalDwt : this.selectedVolumesCargoUnit == "TEU" ? x.totalTeu : x.totalCBM)
                        .sum(x => x);
                }
                chartDataToPush.periods.push({
                    identifier: period,
                    value: periodCargoQtyPerCargoType
                });
            }
            chartData.push(chartDataToPush);
        }

        const maxValue = Enumerable.from(chartData)
            .select(x => Enumerable.from(x.periods).max(y => y.value))
            .max(x => x);

        if (chartData)
            this.cargoVolumesChartData = chartData;

        this.cargoVolumesChartSettings = this.generateChartSettings(maxValue * 1.1, 5);
        this.cargoVolumesChartSettings.legends = this.getChartLegend(periods);
    }

    getCargoVolumesPeriodsData(type: string): any[] {
        if (!this.cargoVolumesChartData)
            return;

        const filteredData = this.cargoVolumesChartData.filter(x => x.cargoType == type)[0];
        if (!filteredData)
            return [];

        return filteredData.periods;
    }

    setVolumesCargoUnit(unit: string) {
        if (!unit)
            return;

        this.selectedVolumesCargoUnit = unit;
        this.initCargoVolumesChart();
    }

    //#endregion

    //#region generic Chart settings

    generateChartSettings(value: number, numberOfSteps: number = 10): ChartSettings {
        const settings: ChartSettings = {
            maxValue: 0,
            step: 0,
            numberOfSteps: numberOfSteps,
            legends: []
        };

        const maxValue = this.computeMaxValue(value);
        const step = Number((maxValue / numberOfSteps).toFixed(0));

        settings.maxValue = Number(maxValue.toFixed(0));
        settings.step = step;
        return settings;
    }

    computeMaxValue(value: number): any {
        let maxValue = value;
        let multiplier = 1;
        let ceilingValue = 100;
        if (maxValue <= 100) {
            ceilingValue = 10;
        }

        while (maxValue > ceilingValue) {
            maxValue = maxValue / 10;
            multiplier = multiplier * 10;
        }

        maxValue = Math.ceil(maxValue) * multiplier;

        // min number of steps is 10 with step of 1
        if (maxValue < 10)
            maxValue = 10;

        return maxValue;
    }

    getBarSize(value: number, maxValue: number, containerSize: number) {
        let result = 0;

        if (value >= maxValue) {
            return 0;
        }

        const valuePercentage = value / maxValue;
        result = containerSize * valuePercentage;

        return result;
    }

    getIntensityBarSize(value: number, maxValue: number, containerSize: number, periodData:{ totalValue:number, quarters:{ identifier:string, value:number }[] }): number {
        //compute % of average vs max average
        const averagePercentage = periodData.totalValue / maxValue;
        //compute container size for average
        const baseContainerSize = containerSize * averagePercentage;
        //compute total value
        let total = Enumerable.from(periodData.quarters).sum(x => x.value);
        total = total ? total : 1;
        //compute % of value vs total
        const valuePercentage = value / total;
        //compute bar size
        const result = baseContainerSize * valuePercentage;
        return result;
    }

    getChartLegend(namesToConvert: string[]): CaChartLegend[] {
        if (!namesToConvert || namesToConvert.length == 0)
            return [];

        const legend: CaChartLegend[] = [];

        namesToConvert.forEach((name, index) => {
            legend.push({ name: name, cssClass: `color${index + 1}` })
        });
        return legend;
    }

    getBarCssClass(legends: CaChartLegend[], name: string): string {
        let result = '';
        result = Enumerable.from(legends).where(x => x.name == name).select(x => x.cssClass).firstOrDefault();
        return result;
    }

    //#endregion

    //#region custom report filters

    customFilterItems: { [proeprty: string]: any[] } = {};

    async getCustomFilters() {
        if (!this.userCustomerId && !this.selectedCustomerId)
            this.userCustomerId = await this.customerService.getCustomerId();
        this.customFilters = await this.carbonAccountingService.GetCustomerCACustomReportFilters(this.selectedCustomerId ? this.selectedCustomerId : this.userCustomerId);
        if (!Array.isArray(this.customFilters))
            this.customFilters = [];
    }

    toggleCustomFilter(filter) {
        filter.open = !filter.open;
        this.selectedFilter = {
            title: filter.displayName,
            clear: () => {
                this.clearCustomFilter(filter);
            },
            apply: () => {
                if (!this.customFilters || this.customFilters.length == 0)
                    return;
                const selectedItem = filter["selectedItem"];
                const index = this.customFilters.indexOf(filter);
                this.applyCustomFilter(index, selectedItem, false);
            }
        }
    }

    clearCustomFilter(filter) {
        filter.selectedValue = null;
        filter.selectedItem = null;
        filter.open = false;

        this.reloadSummaryReportData();
    }

    applyCustomFilter(index, item: LookupValue, reload: boolean = true) {
        if (!this.customFilters || this.customFilters.length == 0)
            return;
        const filter = this.customFilters[index];
        filter["selectedValue"] = item ? item.name : null;
        if (reload == true) this.reloadSummaryReportData();
        this.toggleCustomFilter(filter);
    }

    clearAllFilters() {
        if (this.customFilters && this.customFilters.length > 0)
            this.customFilters.forEach(filter => {
                filter["selectedItem"] = null;
                filter["selectedValue"] = null;
                filter["open"] = false;
            });

        this.yearRangeFilterActive = false;

        this.reloadSummaryReportData();
    }
    //#endregion

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    //#region cargo unit filter
    computedUnits: { id: number, name: string, unit: string }[] = [
        {
            id: 1,
            name: "DWT",
            unit: "t"
        },
        {
            id: 2,
            name: "TEU",
            unit: "teu"
        },
        {
            id: 3,
            name: "CBM",
            unit: "cbm"
        },
    ];

    selectedComputedUnit: { id: number, name: string, unit: string } = this.computedUnits[0];

    computedUnitChanged($event) {
        if (!$event.detail || ($event.detail.oldValue && $event.detail.item.id === $event.detail.oldValue.id))
            return;

        this.initGhgEmissionsChart();
    }

    @computedFrom("selectedComputedUnit")
    get unitOfMeassurment() {
        return this.selectedComputedUnit ? this.selectedComputedUnit.unit : "t";
    }

    //#endregion
}

export interface ChartSettings {
    maxValue: number;
    step: number;
    numberOfSteps: number;
    legends: CaChartLegend[];
}

export interface CaChartLegend {
    name: string;
    cssClass: string
}

export enum PeriodTypeEnum {
    Quarterly = 1,
    Annual = 2
}
